<script setup lang="ts">
  import { Icon, IconName, IconSize, Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { computed } from 'vue';
  import { splitDestinationName, type NameMatch } from '../lib/matchDestinationName';

  interface DestinationMobileRowProps {
    name: string;
    sub?: string;
    subAlert?: boolean;
    badge?: number;
    recent?: boolean;
    locked?: boolean;
    highlight?: string;
  }

  const props = withDefaults(defineProps<DestinationMobileRowProps>(), {
    sub: '',
    subAlert: false,
    badge: 0,
    recent: false,
    locked: false,
    highlight: '',
  });

  interface DestinationMobileRowEmits {
    (e: 'click'): void;
  }

  const emit = defineEmits<DestinationMobileRowEmits>();

  const match = computed<NameMatch | null>(() => splitDestinationName(props.name, props.highlight));
</script>

<template>
  <button
    type="button"
    :class="['destination-mobile-row', { 'destination-mobile-row_locked': locked }]"
    data-testid="destination-mobile-row"
    @click="emit('click')"
  >
    <span
      v-if="recent"
      class="destination-mobile-row__recent"
    >
      <Icon
        :name="IconName.Clock"
        :size="IconSize.M"
      />
    </span>

    <span class="destination-mobile-row__body">
      <Typo
        class="destination-mobile-row__name"
        :variant="TypoVariant.BodyLRegular"
        tag="span"
      >
        <template v-if="match">
          <span>{{ match.before }}</span>
          <b>{{ match.match }}</b>
          <span>{{ match.after }}</span>
        </template>
        <template v-else>{{ name }}</template>
      </Typo>
      <Typo
        v-if="sub"
        :class="['destination-mobile-row__sub', { 'destination-mobile-row__sub_alert': subAlert }]"
        :variant="TypoVariant.BodyXSRegular"
        tag="span"
      >
        {{ sub }}
      </Typo>
    </span>

    <Typo
      v-if="badge > 0"
      class="destination-mobile-row__badge"
      data-testid="destination-mobile-row-badge"
      tag="span"
    >
      {{ badge }}
    </Typo>

    <span class="destination-mobile-row__chevron">
      <Icon
        :name="IconName.ChevronRight"
        :size="IconSize.S"
      />
    </span>
  </button>
</template>

<style scoped lang="scss">
  .destination-mobile-row {
    align-items: center;
    background: none;
    border: none;
    box-sizing: border-box;
    cursor: pointer;
    display: flex;
    font: inherit;
    gap: var(--spacing-xs);
    min-height: 56px;
    padding: var(--spacing-s) var(--spacing-m);
    text-align: left;
    width: 100%;

    &:active {
      background: var(--color-bg-secondary);
    }

    &_locked {
      background: var(--color-blue-025);
    }

    &__recent {
      color: var(--color-text-secondary);
      display: inline-flex;
      flex: 0 0 auto;
    }

    &__body {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      min-width: 0;
    }

    &__name {
      color: var(--color-text-primary);

      b {
        color: var(--color-brand-secondary);
        font-weight: 600;
      }
    }

    &__sub {
      color: var(--color-text-secondary);
      display: block;

      &::first-letter {
        text-transform: uppercase;
      }

      &_alert {
        color: var(--color-error-500);
      }
    }

    &__badge {
      background: var(--color-brand-secondary);
      border-radius: 100%;
      box-sizing: border-box;
      color: var(--color-text-inverse);
      flex: 0 0 auto;
      line-height: 20px;
      min-width: 20px;
      padding: 0 var(--spacing-xxs);
      text-align: center;
    }

    &__chevron {
      color: var(--color-text-tertiary);
      display: inline-flex;
      flex: 0 0 auto;
    }
  }
</style>
